// MediaExtrasRow — Ship 6 quick win #1 (May 2, 2026)
//
// Voice + photo capture row for the canvasser pin sheet. Mirrors the
// "extras" row at the bottom of the close-up bottom-sheet in the prototype.
// Stores blobs/files locally; the pin sheet uploads them when an outcome
// is logged so the storage write happens at the same moment as the
// canvas_visits insert.
//
// Photo: native file input with capture="environment" → on iOS this opens
// the rear camera straight to "Take Photo".
//
// Voice: MediaRecorder in webm/opus (or platform default). Tap mic to
// start; tap again to stop. Shows mm:ss while recording. Auto-stops at
// 60s. Once stopped, shows a tiny preview <audio> + a clear button.

import { useEffect, useRef, useState } from "react";
import { heicToJpeg } from "../../lib/heicToJpeg";

export interface CapturedMedia {
  voiceBlob: Blob | null;
  voiceMs: number;
  photoFile: File | null;
}

// Map a MIME type to a safe storage extension. MediaRecorder default mimeType
// varies by browser: Chrome/Firefox = audio/webm, Safari = audio/mp4. We need
// the saved file's extension to match its content so the storage browser /
// downloads UI behaves correctly.
export function extForBlob(blob: Blob): string {
  const t = (blob.type || "").toLowerCase();
  if (t.includes("webm"))  return "webm";
  if (t.includes("mp4") || t.includes("m4a")) return "m4a";
  if (t.includes("ogg"))   return "ogg";
  if (t.includes("wav"))   return "wav";
  if (t.includes("mpeg"))  return "mp3";
  return "bin";
}

// Resize a photo to max 1280px on the long edge before upload. Saves
// 80-90% of bytes vs raw iPhone HEIC/JPEG. Keeps EXIF orientation by
// drawing through an Image element (which respects orientation in modern
// browsers via the image-orientation CSS rule applied implicitly).
//
// Returns a JPEG blob at quality 0.82 — visually identical for canvasser
// damage shots, ~150-300KB per photo instead of 3-5MB.
export async function compressImage(file: File, maxEdge = 1280, quality = 0.82): Promise<Blob> {
  // Browsers without canvas support → return original
  if (typeof document === "undefined") return file;

  // iPhone HEIC/HEIF → JPEG first. Without this, the <img> decode below fails
  // on non-Apple browsers (which can't decode HEIC), and even on Apple a sub-
  // 1280px HEIC would slip through unconverted and be unviewable in the office.
  // No-op for files that are already JPEG/PNG/etc.
  file = await heicToJpeg(file);

  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const i = new Image();
      i.onload = () => resolve(i);
      i.onerror = () => reject(new Error("image decode failed"));
      i.src = url;
    });

    const longEdge = Math.max(img.naturalWidth, img.naturalHeight);
    // Already small enough — return original (avoids re-encoding overhead)
    if (longEdge <= maxEdge) return file;

    const scale = maxEdge / longEdge;
    const targetW = Math.round(img.naturalWidth * scale);
    const targetH = Math.round(img.naturalHeight * scale);

    const canvas = document.createElement("canvas");
    canvas.width = targetW;
    canvas.height = targetH;
    const ctx = canvas.getContext("2d");
    if (!ctx) return file;
    ctx.drawImage(img, 0, 0, targetW, targetH);

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, "image/jpeg", quality),
    );
    return blob ?? file;
  } catch {
    return file; // fail-open: ship original rather than block submit
  } finally {
    URL.revokeObjectURL(url);
  }
}

const MAX_VOICE_MS = 60_000;

export default function MediaExtrasRow({
  value,
  onChange,
  disabled,
}: {
  value: CapturedMedia;
  onChange: (next: CapturedMedia) => void;
  disabled?: boolean;
}) {
  const [recording, setRecording] = useState(false);
  const [recMs, setRecMs] = useState(0);
  const [recErr, setRecErr] = useState<string | null>(null);
  const recorderRef = useRef<MediaRecorder | null>(null);
  const chunksRef = useRef<Blob[]>([]);
  const startedAtRef = useRef<number>(0);
  const tickerRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const photoInputRef = useRef<HTMLInputElement | null>(null);

  // Cleanup on unmount: stop any active recording + ticker
  useEffect(() => {
    return () => {
      try {
        if (recorderRef.current && recorderRef.current.state !== "inactive") {
          recorderRef.current.stop();
        }
      } catch { /* ignore */ }
      if (tickerRef.current) clearInterval(tickerRef.current);
    };
  }, []);

  async function startRecording() {
    if (disabled) return;
    setRecErr(null);
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        setRecErr("Mic not available on this device");
        return;
      }
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const rec = new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => { if (e.data.size > 0) chunksRef.current.push(e.data); };
      rec.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: rec.mimeType || "audio/webm" });
        const ms = Date.now() - startedAtRef.current;
        // Free the stream
        stream.getTracks().forEach((t) => t.stop());
        onChange({ ...value, voiceBlob: blob, voiceMs: ms });
      };
      rec.start();
      recorderRef.current = rec;
      startedAtRef.current = Date.now();
      setRecMs(0);
      setRecording(true);
      tickerRef.current = setInterval(() => {
        const elapsed = Date.now() - startedAtRef.current;
        setRecMs(elapsed);
        if (elapsed >= MAX_VOICE_MS) stopRecording();
      }, 250);
    } catch (e: any) {
      setRecErr(e?.message ?? "Mic permission denied");
      setRecording(false);
    }
  }

  function stopRecording() {
    try {
      recorderRef.current?.stop();
    } catch { /* ignore */ }
    if (tickerRef.current) { clearInterval(tickerRef.current); tickerRef.current = null; }
    setRecording(false);
  }

  function clearVoice() {
    onChange({ ...value, voiceBlob: null, voiceMs: 0 });
  }

  function onPhotoChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0] ?? null;
    if (file) {
      onChange({ ...value, photoFile: file });
    }
    // reset input so picking the same file twice still fires onchange
    e.target.value = "";
  }

  function clearPhoto() {
    onChange({ ...value, photoFile: null });
  }

  const voicePreviewUrl = value.voiceBlob ? URL.createObjectURL(value.voiceBlob) : null;
  const photoPreviewUrl = value.photoFile ? URL.createObjectURL(value.photoFile) : null;

  return (
    <div className="mt-3 space-y-2">
      <div className="grid grid-cols-2 gap-2">
        {/* Voice button */}
        {value.voiceBlob && voicePreviewUrl ? (
          <div className="flex items-center gap-2 rounded-md border border-purple-200 bg-purple-50 px-2 py-1.5 text-xs text-purple-900">
            <span>🎤</span>
            <audio controls src={voicePreviewUrl} className="h-6 max-w-full flex-1" />
            <button
              type="button"
              onClick={clearVoice}
              className="rounded text-base leading-none text-purple-400 hover:text-purple-700"
              aria-label="Clear voice note"
              title="Remove voice note"
            >×</button>
          </div>
        ) : recording ? (
          <button
            type="button"
            onClick={stopRecording}
            className="flex items-center justify-center gap-2 rounded-md bg-red-500 px-3 py-2 text-xs font-bold text-white shadow"
          >
            <span className="block h-2 w-2 animate-pulse rounded-full bg-white"/>
            <span>Stop · {fmtMs(recMs)}</span>
          </button>
        ) : (
          <button
            type="button"
            onClick={startRecording}
            disabled={disabled}
            className="flex items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            <span className="text-base">🎤</span>
            <span>Voice note</span>
          </button>
        )}

        {/* Photo button */}
        {value.photoFile && photoPreviewUrl ? (
          <div className="flex items-center gap-2 rounded-md border border-emerald-200 bg-emerald-50 px-2 py-1.5 text-xs text-emerald-900">
            <img src={photoPreviewUrl} alt="" className="h-8 w-8 rounded object-cover" />
            <span className="flex-1 truncate font-medium">{value.photoFile.name}</span>
            <button
              type="button"
              onClick={clearPhoto}
              className="rounded text-base leading-none text-emerald-500 hover:text-emerald-800"
              aria-label="Clear photo"
              title="Remove photo"
            >×</button>
          </div>
        ) : (
          <button
            type="button"
            onClick={() => photoInputRef.current?.click()}
            disabled={disabled}
            className="flex items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"
          >
            <span className="text-base">📷</span>
            <span>Photo</span>
          </button>
        )}
        <input
          ref={photoInputRef}
          type="file"
          accept="image/*"
          capture="environment"
          onChange={onPhotoChange}
          className="hidden"
        />
      </div>

      {recErr ? (
        <div className="rounded-md bg-red-50 px-2 py-1 text-[10px] text-red-700">⚠ {recErr}</div>
      ) : null}
    </div>
  );
}

function fmtMs(ms: number): string {
  const s = Math.floor(ms / 1000);
  return `${Math.floor(s / 60)}:${String(s % 60).padStart(2, "0")}`;
}
